import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import api, { ApiError } from '@/api2/client';

/** The API's largest page (ResourceQuery::MAX_PER_PAGE). */
const PER_PAGE = 100;

/**
 * Past this a chart's list is reported as not loaded rather than cut short:
 * 200 pages of 100 is far beyond any chart's paperwork, and a loop that could
 * not end is not a list.
 */
const MAX_PAGES = 200;

const chartKey = (patientId) => (patientId === null || patientId === undefined || patientId === '' ? null : String(patientId));

/**
 * One of the open chart's document lists that pacmny-be shipped inside
 * `getAllData` (the PDFs, the C-CDAs) and the port reads from the API's list
 * of that chart's documents (`GET <path>?filter[patient_id]=`).
 *
 * - **Every page.** pacmny-be's list was the whole set; a chart with more than
 *   one page of documents does not silently lose its oldest.
 * - **Loaded once per chart**, the first time `getAllData` is read for it, and
 *   again only when something says the list changed (`invalidate()`, after
 *   `updateApplicationModel`) or asks for it (`load()`, after an upload). A
 *   load for the chart already being loaded joins that request, so an upload's
 *   own reload and the re-read the invalidation set off are one request, and
 *   one audit row.
 * - **A failed load is not an empty list.** It is remembered, so the page can
 *   say the list could not be loaded, and so it is not retried on every render;
 *   the next invalidation or explicit load tries again.
 * - **An answer for a chart no longer in view is dropped** (a read for another
 *   chart, an invalidation, or a return to a chart already loaded abandons it),
 *   and rows are only ever stored together with the chart they are for, so one
 *   chart's documents never show under another's.
 *
 * @param {string} storeId
 * @param {{path: string, shape: (items: object[]) => object, failedKey: string}} options
 *   `shape` turns the API's rows into the keys `getAllData` carries; `failedKey`
 *   is the flag a failed load sets beside them.
 */
export function defineChartDocumentList(storeId, { path, shape, failedKey }) {
  const useStore = defineStore(storeId, () => {
    /** The chart the rows below belong to. */
    const loadedFor = ref(null);
    const items = ref([]);
    const failed = ref(false);
    /** The rows are a chart's, but something has changed it since. */
    const stale = ref(false);

    // Bookkeeping, not state: these change while a getter is being evaluated,
    // and reactive writes there would re-trigger it.
    let inflight = null;
    /** `{target, abandoned}`: a load `ensureFor` has deferred to a microtask. */
    let requested = null;

    const legacyShape = computed(() => shape(items.value));

    async function readAll(target) {
      const rows = [];
      const seen = new Set();

      for (let page = 1; page <= MAX_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await api.list(path, { 'filter[patient_id]': target, page, per_page: PER_PAGE });

        // The API breaks `uploaded_at` ties by id, so its pages neither repeat
        // nor skip a row while the list stands still; one uploaded between two
        // page reads still shifts the rest by one, so a row is kept once.
        (result.items || []).forEach((row) => {
          const key = row && (row.public_id || row.id);
          if (seen.has(key)) return;
          seen.add(key);
          rows.push(row);
        });

        const last = Number(result.meta && result.meta.last_page) || 1;
        if (page >= last) return rows;
      }

      throw new Error(`${path}: more than ${MAX_PAGES} pages for one chart`);
    }

    function settle(target, rows, didFail) {
      items.value = rows;
      failed.value = didFail;
      loadedFor.value = target;
      stale.value = false;
      inflight = null;
    }

    /** Read the chart's list now (or join the read already under way). Never rejects. */
    function load(patientId) {
      const target = chartKey(patientId);

      if (target === null) {
        clear();
        return Promise.resolve();
      }

      if (inflight && inflight.target === target) return inflight.promise;

      const entry = { target, promise: null };

      // The rows, or the failure, and the chart they belong to are written
      // together, in one callback. Written apart (the rows here, the chart in a
      // later `.finally`), a render flushed between the two microtasks read one
      // chart's rows labelled as the chart still in view: patient 8's PDFs in
      // patient 7's list, and PdfContentBody fetching one into 7's viewer.
      entry.promise = readAll(target)
        .then((rows) => {
          if (inflight !== entry) return;
          settle(target, rows, false);
        })
        .catch((error) => {
          if (inflight !== entry) return;
          // A background read the page never asked for must not surface as an
          // unhandled rejection; it is shown as "could not be loaded" instead.
          if (!(error instanceof ApiError) && process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`[${storeId}] could not load the chart's documents`, error);
          }
          settle(target, [], true);
        });

      inflight = entry;

      return entry.promise;
    }

    /**
     * Load the chart's list unless it is loaded, loading or asked for. Called
     * from a getter, so the request is deferred to a microtask rather than
     * changing state while the getter is being evaluated.
     */
    function ensureFor(patientId) {
      const target = chartKey(patientId);

      if (target === null) return;

      if (target === loadedFor.value && !stale.value) {
        // Served from what is loaded. A read started for another chart (opened,
        // then left before it answered) is abandoned here, and one only asked
        // for is never started: its answer would replace the rows of the chart
        // the clinician came back to.
        if (inflight && inflight.target !== target) inflight = null;
        if (requested && requested.target !== target) {
          requested.abandoned = true;
          requested = null;
        }
        return;
      }
      if ((inflight && inflight.target === target) || (requested && requested.target === target)) return;

      const request = { target, abandoned: false };
      requested = request;
      Promise.resolve()
        .then(() => (request.abandoned ? undefined : load(target)))
        .finally(() => {
          if (requested === request) requested = null;
        });
    }

    /**
     * The list may have changed: re-read it the next time it is read. The rows
     * stay on screen until then, and a read already under way is abandoned,
     * because it may have started before the change.
     */
    function invalidate() {
      stale.value = true;
      inflight = null;
      requested = null;
    }

    function clear() {
      loadedFor.value = null;
      items.value = [];
      failed.value = false;
      stale.value = false;
      inflight = null;
      requested = null;
    }

    return { loadedFor, items, failed, stale, legacyShape, load, ensureFor, invalidate, clear };
  });

  /**
   * The overlay the `login/getAllData` getter spreads: this chart's list and
   * whether it failed to load, once it has been read, and a request for it
   * until then.
   */
  function forGetAllData(patientId) {
    const target = chartKey(patientId);

    if (target === null) return {};

    const store = useStore();

    store.ensureFor(target);

    if (store.loadedFor !== target) return {};

    return { ...store.legacyShape, [failedKey]: store.failed };
  }

  return { useStore, forGetAllData };
}
